'use client';

import type { ColumnFiltersState, Row, RowSelectionState, SortingState, Table as TableInstance } from '@tanstack/react-table';
import { createContext, type ReactNode, type RefObject, useCallback, useContext, useMemo } from 'react';
import type { DataGridExpandedSubItem } from './types';

/**
 * Simplified table state interface for action components.
 * Exposes only the most commonly needed attributes without requiring
 * knowledge of react-table internals.
 */
export interface DataGridTableState {
  // ========== Row Management ==========
  /** Total number of rows after filtering is applied (across all pages) */
  totalRows: number;
  /** Number of rows on the current page */
  pageRowCount: number;

  // ========== Loading & UI State ==========
  /** Whether the table is in a loading state */
  isLoading: boolean;

  // ========== Pagination ==========
  /** Current page index (0-based) */
  pageIndex: number;
  /** Current page size */
  pageSize: number;
  /** Total number of pages */
  pageCount: number;

  // ========== Filtering & Search ==========
  filters: {
    /** Array of active column filters */
    columnFilters: ColumnFiltersState;
    /** Current global filter/search term */
    globalFilter?: string;
  };
  /** Whether any filters or search are currently applied */
  hasFiltersActive: boolean;

  // ========== Sorting ==========
  /** Array of active sort configurations */
  sorting: SortingState;

  // ========== Selection & Visibility ==========
  /** Current row selection state (prepared for future row selection features) */
  rowSelection: RowSelectionState;
  /** Selected row data objects */
  selectedRows: unknown[];
  /** Count of selected rows */
  selectedRowCount: number;
  /** Selected expanded sub-items: rowId -> subItemId -> data */
  selectedExpandedSubItems: Record<string, Record<string, unknown>>;
  /** Count of selected sub-items */
  selectedExpandedSubItemCount: number;
  /** Total count of all selections (rows + sub-items) */
  totalSelectedCount: number;
  /** Whether any selections exist */
  hasSelection: boolean;
}

export interface DataGridContextValue<TData = unknown> {
  tableRef: RefObject<HTMLTableElement | null>;
  tableTitleRef: RefObject<HTMLHeadingElement | null>;
  returnFocusToTable: () => void;
  returnFocusToTitleOrTable: () => void;
  /** Simplified table state (e.g., for action components) */
  tableState: DataGridTableState;
  /** The raw table instance for more custom or advanced use cases */
  tableInstance?: TableInstance<TData>;
}

export interface DataGridProviderProps<TData = unknown> {
  value: DataGridContextValue<TData>;
  children?: ReactNode;
}

const DataGridContext = createContext<DataGridContextValue<unknown> | null>(null);

/**
 * Wrapper component that accepts generic TData.
 * Uses boundary casting to work around React.Context type variance limitations.
 */
export function DataGridProvider<TData = unknown>({ value, children }: DataGridProviderProps<TData>) {
  return <DataGridContext.Provider value={value as DataGridContextValue<TData | unknown>}>{children}</DataGridContext.Provider>;
}

/**
 * Hook to consume the DataGrid context. Must be used within a DataGridProvider.
 */
export function useDataGridContext<TData = unknown>() {
  const context = useContext(DataGridContext) as DataGridContextValue<TData>;
  if (!context) {
    throw new Error('useDataGridContext must be used within a DataGridProvider');
  }
  return context;
}

interface UseDataGridProviderValueArgs<TData = unknown> {
  table: TableInstance<TData>;
  isLoading: boolean;
  internalTableRef: RefObject<HTMLTableElement | null>;
  tableTitleRef: RefObject<HTMLHeadingElement | null>;
  expandedSubItemSelection: Record<string, Record<string, boolean>>;
  getSelectableSubItems?: (row: Row<TData>) => DataGridExpandedSubItem[];
}

/**
 * Hook that creates the DataGridContextValue, including the tableState and focus callbacks.
 * This abstraction encapsulates all context value creation logic in one place.
 *
 * @param args - Configuration object with table instance and refs
 * @returns The complete context value ready to pass to DataGridProvider
 */
export function useDataGridProviderValue<TData = unknown>(args: UseDataGridProviderValueArgs<TData>): DataGridContextValue<TData> {
  const { table, isLoading, internalTableRef, tableTitleRef, expandedSubItemSelection, getSelectableSubItems } = args;

  const returnFocusToTable = useCallback(() => {
    requestAnimationFrame(() => {
      internalTableRef.current?.focus();
    });
  }, [internalTableRef]);

  const returnFocusToTitleOrTable = useCallback(() => {
    requestAnimationFrame(() => {
      // Try to focus table title first, then table element as fallback
      if (tableTitleRef.current) {
        tableTitleRef.current.focus();
      } else {
        internalTableRef.current?.focus();
      }
    });
  }, [internalTableRef, tableTitleRef]);

  const tableStateSnapshot = table.getState();
  const filteredRowModel = table.getFilteredRowModel();

  const selectedRowModel = table.getSelectedRowModel();
  const selectedRows = useMemo(() => selectedRowModel.rows.map((row) => row.original), [selectedRowModel]);
  const selectedRowCount = selectedRows.length;

  const selectedExpandedSubItems = useMemo(() => {
    const result: Record<string, Record<string, unknown>> = {};

    for (const [rowId, subItemMap] of Object.entries(expandedSubItemSelection)) {
      // Use rowsById instead of table.getRow() — getRow() throws on missing IDs whereas rowsById returns undefined
      const row = table.getRowModel().rowsById[rowId];
      if (!row || !getSelectableSubItems) continue;

      const subItems = getSelectableSubItems(row);
      const selected: Record<string, unknown> = {};

      for (const [subItemId, isSelected] of Object.entries(subItemMap)) {
        if (isSelected) {
          const subItem = subItems.find((sr) => sr.id === subItemId);
          if (subItem) selected[subItemId] = subItem.data;
        }
      }

      if (Object.keys(selected).length > 0) {
        result[rowId] = selected;
      }
    }

    return result;
  }, [expandedSubItemSelection, getSelectableSubItems, table]);

  const selectedExpandedSubItemCount = Object.values(selectedExpandedSubItems).reduce((sum, row) => sum + Object.keys(row).length, 0);

  // Counts selected parent rows across all pages via rowSelection state, not the current page's row model.
  // Intentionally excludes sub-row selection — sub-rows are not yet used in bulk actions and will
  // require dedicated support if that use case is added.
  const totalSelectedCount = Object.values(tableStateSnapshot.rowSelection).filter(Boolean).length;
  const hasSelection = totalSelectedCount > 0;

  const tableState: DataGridTableState = useMemo(
    () => ({
      // Row counts
      totalRows: filteredRowModel.rows.length,
      pageRowCount: table.getRowModel().rows.length,

      // Loading state
      isLoading,

      // Pagination
      pageIndex: tableStateSnapshot.pagination.pageIndex,
      pageSize: tableStateSnapshot.pagination.pageSize,
      pageCount: table.getPageCount(),

      // Filters & search
      filters: {
        columnFilters: tableStateSnapshot.columnFilters,
        globalFilter: typeof tableStateSnapshot.globalFilter === 'string' ? tableStateSnapshot.globalFilter : undefined,
      },
      hasFiltersActive:
        tableStateSnapshot.columnFilters.length > 0 ||
        (tableStateSnapshot.globalFilter !== '' && tableStateSnapshot.globalFilter !== undefined),

      // Sorting
      sorting: tableStateSnapshot.sorting,

      // Selection
      rowSelection: tableStateSnapshot.rowSelection,
      selectedRows,
      selectedRowCount,
      selectedExpandedSubItems,
      selectedExpandedSubItemCount,
      totalSelectedCount,
      hasSelection,
    }),
    [
      filteredRowModel.rows.length,
      table,
      tableStateSnapshot,
      isLoading,
      selectedRows,
      selectedRowCount,
      selectedExpandedSubItems,
      selectedExpandedSubItemCount,
      totalSelectedCount,
      hasSelection,
    ],
  );

  const contextValue = useMemo<DataGridContextValue<TData>>(
    () => ({
      tableRef: internalTableRef,
      tableTitleRef,
      returnFocusToTable,
      returnFocusToTitleOrTable,
      tableState,
      tableInstance: table,
    }),
    [internalTableRef, tableTitleRef, returnFocusToTable, returnFocusToTitleOrTable, tableState, table],
  );

  return contextValue;
}
